import { CreateQuoteRequestSuggestionRequest } from 'lib/api/createQuoteRequestSuggestion';
import { resolveAddress } from 'lib/api/resolveAddress';
import { Stop } from 'types/QuoteRequest';
import { Undef } from 'types/UtilityTypes';
import { QuotingPortals } from 'types/enums/Integrations';

/**
 * Returns true when a state value is absent or contains a known placeholder
 * that Transporeon emits when state data is unavailable (e.g. an empty string,
 * "NA", "N/A", or "-").
 *
 * Accepts `undefined` to guard safely against runtime data arriving over Chrome
 * message passing where optional fields may be missing despite TypeScript types.
 */
export function isMissingState(value: Undef<string>): boolean {
  if (!value) {
    return true;
  }

  const normalized = value.trim().toUpperCase();
  return (
    normalized === '' ||
    normalized === 'NA' ||
    normalized === 'N/A' ||
    normalized === '-'
  );
}

/**
 * For Transporeon quote requests that are missing pickup or delivery state,
 * calls the `/locations/resolve` endpoint to fill in the state (and any other
 * missing address fields) using city + zip. Returns the original `data` object
 * unchanged for non-Transporeon sources, or when all states are already present.
 *
 * The portal guard lives here so other portals can be added in the future
 * without touching `QuoteSidebar`.
 *
 * Fails open: if the resolve API call fails, the original (unresolved) data
 * is returned so the quote request can still proceed.
 */
export async function resolveTransporeonMissingStates(
  data: CreateQuoteRequestSuggestionRequest
): Promise<CreateQuoteRequestSuggestionRequest> {
  try {
    if (data.source !== QuotingPortals.Transporeon) {
      return data;
    }

    const needsPickupState =
      isMissingState(data.pickupState) && !!data.pickupCity && !!data.pickupZip;
    const needsDeliveryState =
      isMissingState(data.deliveryState) &&
      !!data.deliveryCity &&
      !!data.deliveryZip;

    if (!needsPickupState && !needsDeliveryState) {
      return data;
    }

    const pickupCountry = data.stops?.[0]?.address?.country || 'US';
    const deliveryCountry =
      data.stops?.[data.stops.length - 1]?.address?.country || 'US';

    const [pickupResolved, deliveryResolved] = await Promise.all([
      needsPickupState
        ? resolveAddress({
            city: data.pickupCity,
            zip: data.pickupZip,
            country: pickupCountry,
          })
        : Promise.resolve(null),
      needsDeliveryState
        ? resolveAddress({
            city: data.deliveryCity,
            zip: data.deliveryZip,
            country: deliveryCountry,
          })
        : Promise.resolve(null),
    ]);

    const nextData: CreateQuoteRequestSuggestionRequest = { ...data };
    const nextStops: Stop[] = data.stops ? [...data.stops] : [];

    if (pickupResolved && pickupResolved.isOk()) {
      const pickupAddress = pickupResolved.value;
      if (isMissingState(nextData.pickupState) && pickupAddress.state) {
        nextData.pickupState = pickupAddress.state;
      }

      const shouldUpdatePickupStop =
        nextStops.length > 1 || nextStops[0]?.stopType !== 'dropoff';
      if (nextStops.length > 0 && shouldUpdatePickupStop) {
        const pickupStopState = nextStops[0].address.state;
        nextStops[0] = {
          ...nextStops[0],
          address: {
            ...nextStops[0].address,
            city: nextStops[0].address.city || pickupAddress.city || '',
            state: isMissingState(pickupStopState)
              ? pickupAddress.state || ''
              : pickupStopState,
            zip: nextStops[0].address.zip || pickupAddress.zip || '',
            country:
              nextStops[0].address.country || pickupAddress.country || 'US',
          },
        };
      }
    }

    if (deliveryResolved && deliveryResolved.isOk()) {
      const deliveryAddress = deliveryResolved.value;

      if (isMissingState(nextData.deliveryState) && deliveryAddress.state) {
        nextData.deliveryState = deliveryAddress.state;
      }

      const shouldUpdateDeliveryStop =
        nextStops.length > 1 || nextStops[0]?.stopType !== 'pickup';

      if (nextStops.length > 0 && shouldUpdateDeliveryStop) {
        const deliveryStopIndex = nextStops.length - 1;
        const deliveryStopState = nextStops[deliveryStopIndex].address.state;
        nextStops[deliveryStopIndex] = {
          ...nextStops[deliveryStopIndex],
          address: {
            ...nextStops[deliveryStopIndex].address,
            city:
              nextStops[deliveryStopIndex].address.city ||
              deliveryAddress.city ||
              '',
            state: isMissingState(deliveryStopState)
              ? deliveryAddress.state || ''
              : deliveryStopState,
            zip:
              nextStops[deliveryStopIndex].address.zip ||
              deliveryAddress.zip ||
              '',
            country:
              nextStops[deliveryStopIndex].address.country ||
              deliveryAddress.country ||
              'US',
          },
        };
      }
    }

    if (nextStops.length > 0) {
      nextData.stops = nextStops;
    }

    return nextData;
  } catch {
    // Fail open: runtime errors must not block quote suggestion creation.
    return data;
  }
}
